import type { Site, User, Cluster, ClusterDetail, ClusterNote, CameraStatus } from './types'

async function apiFetch(url: string, opts?: RequestInit): Promise<Response> {
  const res = await fetch(url, opts)
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Unauthorized')
  }
  return res
}

export async function fetchMe(): Promise<User | null> {
  const res = await fetch('/api/me')
  if (res.status === 401) return null
  if (!res.ok) return null
  return res.json()
}

// fetchSignInTheme is the theme for pages drawn before anyone signs in, which
// have no /api/me to read it from. Undefined when VMS does not say.
export async function fetchSignInTheme(): Promise<string | undefined> {
  try {
    const res = await fetch('/auth/theme')
    if (!res.ok) return undefined
    return (await res.json()).theme || undefined
  } catch {
    return undefined
  }
}

// Session is what /api/me says of the session: signed in, gone, or not known
// — a server error or no connection, which is no reason to sign anyone out.
export type Session = { state: 'signed-in'; user: User } | { state: 'signed-out' } | { state: 'unreachable' }

export async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch('/api/me')
    if (res.status === 401) return { state: 'signed-out' }
    if (!res.ok) return { state: 'unreachable' }
    return { state: 'signed-in', user: await res.json() }
  } catch {
    return { state: 'unreachable' }
  }
}

export async function fetchSites(): Promise<Site[]> {
  const res = await apiFetch('/api/sites')
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

// Resolved by the backend rather than read from go2rtc directly: a Frigate
// site's cameras come from go2rtc's stream list, an ONVIF site's from
// onvif-monitor's registry, and only the backend knows which a site is.
export async function fetchCameras(siteId: string): Promise<string[]> {
  const res = await fetch(`/api/sites/${encodeURIComponent(siteId)}/cameras`)
  if (!res.ok) throw new Error(`${res.status}`)
  return await res.json()
}

/** A camera's current picture, and when it was taken, in seconds since the epoch. */
export interface LiveStill {
  url: string
  at: number
}

// Fetched rather than set as an <img> src, because the tile writes the
// frame's age and only the response's Last-Modified knows it: the server
// shares one frame per camera across every open wall. The URL holds the
// picture until releaseStill.
export async function fetchStill(siteId: string, camera: string): Promise<LiveStill> {
  const res = await fetch(`/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(camera)}/still`)
  if (!res.ok) throw new Error(`${res.status}`)
  const taken = Date.parse(res.headers.get('Last-Modified') ?? '')
  const blob = await res.blob()
  return { url: URL.createObjectURL(blob), at: Math.floor((Number.isNaN(taken) ? Date.now() : taken) / 1000) }
}

export function releaseStill(still: LiveStill): void {
  URL.revokeObjectURL(still.url)
}

// Camera up/down, keyed by camera name. Kept off /cameras deliberately: mobile
// consumes that endpoint and renders the names directly, so changing its shape
// would break already-installed builds.
export async function fetchCameraHealth(siteId: string): Promise<Record<string, CameraStatus>> {
  const res = await fetch(`/api/sites/${encodeURIComponent(siteId)}/health`)
  if (!res.ok) throw new Error(`${res.status}`)
  return normaliseHealth(await res.json())
}

const KNOWN_STATUSES: readonly string[] = ['online', 'offline', 'degraded', 'unknown']

/**
 * Maps any status this build was not compiled against to `unknown`.
 *
 * The server gains camera states over time, and a build already in a browser —
 * or on a phone — cannot be corrected when it does. `unknown` is the right
 * answer for one: it already means "no health source, or still settling", and
 * renders as an ordinary camera rather than a fault.
 *
 * Nothing switches exhaustively on CameraStatus today, so an unrecognised
 * value happens to render harmlessly. That is an accident of how the consumers
 * are written, not a property of the type — this makes it the latter.
 */
export function normaliseHealth(raw: unknown): Record<string, CameraStatus> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, CameraStatus> = {}
  for (const [camera, status] of Object.entries(raw as Record<string, unknown>)) {
    out[camera] = KNOWN_STATUSES.includes(status as string) ? (status as CameraStatus) : 'unknown'
  }
  return out
}

/** A camera whose view has been interfered with, and the incident that said so. */
export interface CameraTamper {
  since: string
  cluster_id?: number
  /** Every kind of interference holding the state, obscured first. Absent from
   * an older server. */
  signals?: TamperSignal[]
}

/** Two kinds, because they point at different fixes: uncover, or re-aim. */
export type TamperSignal = 'obscured' | 'moved'


/**
 * The cameras at a site whose view is currently interfered with.
 *
 * A separate axis from health, and a separate endpoint: a blinded camera is
 * still streaming, so its health is genuinely `online`. An older server has no
 * such endpoint, so a failure means "nothing known to be tampered" rather than
 * an error a dashboard has to handle.
 */
export async function fetchTamper(siteId: string): Promise<Record<string, CameraTamper>> {
  const res = await fetch(`/api/sites/${encodeURIComponent(siteId)}/tamper`)
  if (!res.ok) return {}
  const raw: unknown = await res.json()
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {}
  return raw as Record<string, CameraTamper>
}

/**
 * Records a camera's current view as the correct one.
 *
 * Not a dismiss: the state clears because the comparison then passes, which is
 * why this is audited — an operator who uses it on a genuinely blocked camera
 * has told the system a bag over the lens is normal.
 */
export async function acceptCameraView(siteId: string, camera: string): Promise<void> {
  const res = await fetch(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(camera)}/accept-view`,
    { method: 'POST' },
  )
  if (!res.ok) throw new Error(`${res.status}`)
}

export async function fetchClusters(limit = 100, offset = 0): Promise<Cluster[]> {
  const res = await apiFetch(`/api/clusters?limit=${limit}&offset=${offset}`)
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export async function fetchClusterDetail(id: number): Promise<ClusterDetail> {
  const res = await apiFetch(`/api/clusters/${id}`)
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export async function updateClusterAck(id: number, acknowledged: boolean): Promise<Cluster> {
  const res = await apiFetch(`/api/clusters/${id}/ack`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ acknowledged }),
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export async function addClusterNote(id: number, text: string): Promise<ClusterNote> {
  const res = await apiFetch(`/api/clusters/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export function imageUrl(id: number, eventTime: number): string {
  return `/api/events/${id}/image?t=${eventTime}`
}

export function streamUrl(siteId: string, camera: string): string {
  return `/${encodeURIComponent(camera + '_at_' + siteId)}`
}

// 15s pre-roll so dispatchers see the lead-up, not just the trigger frame.
//
// With no part, the backend serves the stretch overlapping the incident most —
// which is what this returned before parts existed, so an older client is
// unaffected. See clipPartUrl for one numbered stretch.
export function clipUrl(clusterId: number): string {
  return `/api/clusters/${clusterId}/clip`
}

// One numbered stretch of an incident's footage. Separate from clipUrl so that
// keeps returning exactly what mobile's does — urlParity.test.ts compares the
// two by source, and it caught this the first time round.
export function clipPartUrl(clusterId: number, part: number): string {
  return `${clipUrl(clusterId)}?part=${part}`
}

/**
 * The stretches of footage an incident spans.
 *
 * An ONVIF recorder writes a clip per stretch of motion, so an incident that
 * spans a pause spans several. The gaps between them are real — that footage
 * was never recorded — so they are played in sequence rather than stitched.
 */
export interface ClipPart {
  index: number
  start: number
  end: number
  duration: number
  camera: string
  /**
   * Which continuous run of footage this part belongs to. Parts sharing one
   * play through without a hole; a change of stretch is footage that was
   * never recorded. Counted instead of parts because the recorder writes a
   * file per trigger — one measured incident returned 122 files covering
   * three runs. Absent from a server that predates this.
   */
  stretch?: number
}

// NoRecordingsError is the clip listing saying the site keeps no recordings
// VMS can reach (409), as opposed to no footage for this incident.
export class NoRecordingsError extends Error {
  constructor() {
    super('this site keeps no recordings')
    this.name = 'NoRecordingsError'
  }
}

export async function fetchClipParts(clusterId: number): Promise<ClipPart[]> {
  const res = await apiFetch(`/api/clusters/${clusterId}/clips`)
  if (res.status === 409) throw new NoRecordingsError()
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}


export type SpeakResult =
  // missed names the paired speakers that did not answer; the rest played it.
  | { outcome: 'played'; missed?: string[] }
  | { outcome: 'stopped' }
  | { outcome: 'failed'; reason: string }

// speak has a camera say text. It resolves when the message has finished
// playing — up to half a minute — and never rejects: every refusal carries the
// server's own reason, and aborting `signal` is the dispatcher's Stop.
export async function speak(siteId: string, camera: string, text: string, signal?: AbortSignal): Promise<SpeakResult> {
  let res: Response
  try {
    res = await apiFetch(`/api/speak?site=${encodeURIComponent(siteId)}&camera=${encodeURIComponent(camera)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      signal,
    })
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return { outcome: 'stopped' }
    return { outcome: 'failed', reason: 'Cannot reach the server.' }
  }
  const body = await res.json().catch(() => ({}))
  if (res.ok) return Array.isArray(body.missed) && body.missed.length > 0 ? { outcome: 'played', missed: body.missed } : { outcome: 'played' }
  return { outcome: 'failed', reason: typeof body.error === 'string' ? body.error : `The server answered ${res.status}.` }
}

export type SpeakerKind = 'microphone' | 'message'

// speech is whether the server can speak typed messages at all; a server
// from before it was reported leaves it out.
export type SpeakerState =
  | { speaking: false; speech?: boolean }
  | { speaking: true; speaker: string; kind: SpeakerKind; since: string; yours: boolean; speech?: boolean }

/** Whether a camera can be heard: its own talk channel or a paired IP speaker. */
export async function fetchTalk(siteId: string, camera: string): Promise<{ talk: boolean }> {
  const res = await apiFetch(`/api/talk?site=${encodeURIComponent(siteId)}&camera=${encodeURIComponent(camera)}`)
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export async function fetchSpeaker(siteId: string, camera: string): Promise<SpeakerState> {
  const res = await apiFetch(`/api/speaker?site=${encodeURIComponent(siteId)}&camera=${encodeURIComponent(camera)}`)
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export interface Phrase {
  id: number
  text: string
}

export async function fetchPhrases(): Promise<Phrase[]> {
  const res = await apiFetch('/api/phrases')
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

// A camera on a site's plan. x and y are fractions of the plan's width and
// height; direction is degrees clockwise from the top and fov how wide the
// camera sees, each null when not set.
export interface MapPlacement {
  camera: string
  x: number
  y: number
  direction: number | null
  fov: number | null
  // reach is how far its cone extends, as a share of the plan's width; an
  // older server sends none.
  reach?: number | null
  place: string
}

export interface SiteMap {
  has_plan: boolean
  width?: number
  height?: number
  updated_at?: number
  placements: MapPlacement[]
}

export async function fetchSiteMap(siteId: string): Promise<SiteMap> {
  const res = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}/map`)
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

// The version makes a replaced plan a new address, so an <img> showing the
// old one fetches the new one rather than keeping what it has.
export function siteMapImageUrl(siteId: string, version: number | undefined): string {
  return `/api/sites/${encodeURIComponent(siteId)}/map/image?v=${version ?? 0}`
}

// ArmState is whether a site is armed, and the next change its schedule will
// make; next is null when it has no schedule.
export interface ArmState {
  armed: boolean
  next: { action: 'arm' | 'disarm'; at: string } | null
  // changed is who last armed or disarmed the site and when; a server from
  // before it was reported leaves it out.
  changed?: { by: 'user' | 'schedule'; who?: string; at: string } | null
}

// fetchArm rejects on an older VMS, which has no arm state to show.
export async function fetchArm(siteId: string): Promise<ArmState> {
  const res = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}/arm`)
  if (!res.ok) throw new Error(`${res.status}`)
  const body = await res.json()
  return { armed: body.armed, next: body.next ?? null, changed: body.changed ?? null }
}

export async function setArmed(siteId: string, armed: boolean): Promise<void> {
  const res = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}/arm`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ armed }),
  })
  if (!res.ok) throw new Error(`${res.status}`)
}

// postponeArm moves a site's next auto-arm later by 30 or 60 minutes, and
// resolves with when it now happens, as VMS answers.
export async function postponeArm(siteId: string, minutes: 30 | 60): Promise<{ at?: string }> {
  const res = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}/arm/postpone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ minutes }),
  })
  if (!res.ok) {
    // A 409 is VMS saying the postpone is no longer possible, in its words,
    // which "Try again" would contradict (#248).
    const body = await res.json().catch(() => ({}))
    throw Object.assign(new Error(`${res.status}`), { reason: res.status === 409 && typeof body.error === 'string' ? body.error : undefined })
  }
  return res.json().catch(() => ({}))
}
